/**
 * «Usar mi ubicación»: pedirle al aparato dónde está y guardarlo.
 *
 * PARA QUÉ. Más adelante, buscar retos por distancia: quién anda cerca para
 * jugar el sábado. Hoy solo se guarda, y se dice para qué — pedir un permiso
 * sin explicar qué se hace con lo que se obtiene es cómo se pierde la
 * confianza de alguien de una vez y para siempre.
 *
 * NO SE PIDE SOLO. El permiso se pide cuando la persona aprieta el botón,
 * nunca al abrir la pantalla: un cartel del navegador que aparece sin que
 * nadie haya pedido nada se cierra sin leerlo, y después ya no se puede
 * volver a preguntar.
 *
 * LO QUE SE GUARDA ESTÁ RECORTADO A 110 METROS, y eso lo hace el servidor
 * (ver `checkCoordinates`). Acá se manda lo que el aparato dio; el recorte no
 * puede vivir en la pantalla, porque la pantalla es justamente la parte que
 * cualquiera puede saltarse.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlayerProfile } from '@tennisup/contracts';

import { Alert } from './Alert';
import { PinIcon } from './icons';
import { useDeviceLocation } from '../hooks/useDeviceLocation';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function LocationBox({
  profile,
  onChanged,
}: {
  profile: PlayerProfile;
  onChanged: (profile: PlayerProfile) => void;
}): JSX.Element {
  const { t } = useTranslation();
  // Pedirle la ubicación al aparato es lo mismo acá y en el registro: una
  // sola versión de esas rarezas, en `useDeviceLocation`.
  const aparato = useDeviceLocation();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = guardando || aparato.busy;
  const problema = error ?? aparato.error;
  const tiene = profile.latitude !== null && profile.longitude !== null;

  function pedir(): void {
    setError(null);
    aparato.pedir((location) => void guardar(location.latitude, location.longitude));
  }

  async function guardar(latitude: number, longitude: number): Promise<void> {
    setGuardando(true);
    try {
      onChanged(await api.setLocation(latitude, longitude));
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setGuardando(false);
    }
  }

  async function borrar(): Promise<void> {
    setGuardando(true);
    setError(null);
    try {
      onChanged(await api.removeLocation());
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="location-box">
      <h3>{t('location.title')}</h3>
      <p className="help">{t('location.help')}</p>

      {problema !== null && <Alert kind="error">{problema}</Alert>}

      {tiene && (
        <p className="location-current">
          <PinIcon title={t('location.title')} />
          {/* Las coordenadas se MUESTRAN, y a propósito: es el dato de la
              persona y tiene derecho a ver exactamente qué se guardó. Tres
              decimales es lo que hay en la base, ni más ni menos. */}
          <span>
            {profile.latitude?.toFixed(3)}, {profile.longitude?.toFixed(3)}
          </span>
          {profile.locationTakenAt !== null && (
            <span className="help">{fecha(profile.locationTakenAt)}</span>
          )}
        </p>
      )}

      {/*
        UNA COORDENADA SIN FECHA ES UNA QUE NADIE MIDIÓ.

        Las cuentas anteriores al botón de abajo recibieron un punto repartido
        por provincia (`pnpm ubicaciones`), para que aparecieran en la
        búsqueda de retos en vez de ser invisibles. Ese punto no es una
        medición y acá se dice con esas palabras: quien lo lee entiende por
        qué la distancia que ve puede estar equivocada, y qué botón la
        arregla. Callarlo sería dejar un dato inventado pareciendo bueno.
      */}
      {tiene && profile.locationTakenAt === null && (
        <p className="help">{t('location.approximate')}</p>
      )}

      <div className="button-row">
        <button type="button" disabled={busy} onClick={pedir}>
          {busy ? t('location.asking') : tiene ? t('location.update') : t('location.use')}
        </button>
        {tiene && (
          <button type="button" className="link" disabled={busy} onClick={() => void borrar()}>
            {t('location.remove')}
          </button>
        )}
      </div>
    </div>
  );
}

function fecha(iso: string): string {
  const cuando = new Date(iso);
  return Number.isNaN(cuando.getTime()) ? '' : cuando.toLocaleDateString();
}
